import { useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

const TESTIMONIALS = [
  {
    name: 'María García Sánchez',
    role: 'Empresaria',
    location: 'Madrid',
    text: 'Gracias a Mediatio Global resolvimos un conflicto societario que amenazaba con destruir la empresa que habíamos construido durante 15 años. En solo 3 sesiones llegamos a un acuerdo que nos dejó a todos satisfechos. Increíblemente profesionales.',
    rating: 5,
    image: 'https://public.readdy.ai/ai/img_res/b7112d3203fd51e14d7f9a71497f0157.jpg',
    service: 'Mediación Mercantil',
  },
  {
    name: 'Carlos Rodríguez Pérez',
    role: 'Padre de familia',
    location: 'Barcelona',
    text: 'El proceso de divorcio es siempre duro, pero con la ayuda del equipo de mediación pudimos llegar a un acuerdo de custodia que prioriza el bienestar de nuestros hijos. Lo recomiendo a cualquier familia que pase por una situación similar.',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=professional%20spanish%20man%20portrait%20friendly%20smile%2040s%20casual%20business%20clean%20background%20neutral&width=200&height=200&seq=test002&orientation=squarish',
    service: 'Mediación Familiar',
  },
  {
    name: 'Laura Martínez López',
    role: 'Trabajadora autónoma',
    location: 'Valencia',
    text: 'Tenía un conflicto con mi arrendador que parecía no tener solución. El mediador fue imparcial, empático y muy eficaz. En dos semanas resolvimos lo que llevaba más de un año enquistado. Sin juzgado, sin estrés, sin gastos desorbitados.',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=professional%20spanish%20woman%20portrait%2035s%20warm%20smile%20freelancer%20clean%20background%20neutral%20light&width=200&height=200&seq=test003&orientation=squarish',
    service: 'Mediación Civil',
  },
  {
    name: 'Javier Romero Castillo',
    role: 'Director de RRHH',
    location: 'Sevilla',
    text: 'Contratamos los servicios de mediación laboral para resolver un conflicto entre un empleado y su responsable de área. El resultado fue excelente: ambas partes quedaron satisfechas y el clima laboral mejoró notablemente. Volveremos a contar con ellos sin duda.',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=professional%20hr%20director%20man%20corporate%20portrait%2045s%20friendly%20confident%20suit%20spain%20clean%20background&width=200&height=200&seq=test004&orientation=squarish',
    service: 'Mediación Laboral',
  },
];

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <i key={i} className="ri-star-fill text-amber-400 text-sm" />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const current = TESTIMONIALS[active];
  const { ref, isVisible } = useScrollReveal<HTMLElement>();

  return (
    <section ref={ref} id="testimonios" className="py-28 bg-[#F4F7FA]">
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`text-center mb-14 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: isVisible ? '100ms' : '0ms' }}
        >
          <span className="text-[#3D9E8C] text-sm font-semibold tracking-widest uppercase">Testimonios</span>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold text-[#1B3A5C] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Lo que dicen<br />nuestros clientes
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
          {/* Main testimonial */}
          <div
            className={`lg:col-span-3 bg-white rounded-2xl p-10 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: isVisible ? '250ms' : '0ms' }}
          >
            <div className="flex items-center gap-2 mb-6">
              <i className="ri-double-quotes-l text-[#3D9E8C] text-4xl leading-none" />
            </div>
            <p className="text-[#1B3A5C] text-xl leading-relaxed font-medium mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
              {current.text}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                  <img src={current.image} alt={current.name} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="font-bold text-[#1B3A5C] text-sm">{current.name}</p>
                  <p className="text-gray-500 text-xs">{current.role} · {current.location}</p>
                  <StarRating count={current.rating} />
                </div>
              </div>
              <span className="px-3 py-1.5 bg-[#E8F4F1] text-[#3D9E8C] text-xs font-semibold rounded-full">
                {current.service}
              </span>
            </div>
          </div>

          {/* Selector list */}
          <div
            className={`lg:col-span-2 flex flex-col gap-4 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: isVisible ? '400ms' : '0ms' }}
          >
            {TESTIMONIALS.map((t, i) => (
              <button
                key={t.name}
                onClick={() => setActive(i)}
                className={`flex items-center gap-4 p-4 rounded-xl text-left transition-all duration-200 cursor-pointer ${
                  i === active
                    ? 'bg-[#1B3A5C] text-white'
                    : 'bg-white text-[#1B3A5C] hover:bg-[#E8F4F1]'
                }`}
              >
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="min-w-0">
                  <p className={`font-semibold text-sm truncate ${i === active ? 'text-white' : 'text-[#1B3A5C]'}`}>
                    {t.name}
                  </p>
                  <p className={`text-xs truncate ${i === active ? 'text-white/70' : 'text-gray-400'}`}>
                    {t.service}
                  </p>
                </div>
                <i className={`ri-arrow-right-s-line ml-auto text-lg ${i === active ? 'text-[#3D9E8C]' : 'text-gray-300'}`} />
              </button>
            ))}

            <div className="mt-2 flex items-center gap-3 p-4 bg-[#E8F4F1] rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#3D9E8C]">
                <i className="ri-google-fill text-white text-lg" />
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-xs" />
                  ))}
                  <span className="text-[#1B3A5C] font-bold text-sm ml-1">4,9</span>
                </div>
                <p className="text-gray-500 text-xs">+120 reseñas en Google</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
